/* eslint-disable react/no-unknown-property */
import { useTranslation } from '@pypestream/translations';
import { useCallback, useEffect, useRef, useState } from 'react';
import { sendUserMessage } from '../xstate/app.xstate-utils';

/** What the script-upload screens have always allowed. */
const DEFAULT_MAX_FILE_SIZE_BYTES = 4_194_304;

type InputProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  'test-id'?: string;
  /** Overrides the 4MB default — a retina screenshot routinely exceeds it. */
  maxFileSizeBytes?: number;
};

/**
 * A hidden file input plus the ref that opens it.
 *
 * The `change` listener is attached to the DOM node itself rather than through
 * React's `onChange`, and that is load-bearing rather than a style choice.
 * `ps-modal` re-parents its whole subtree into `ps-portal-destination`, a
 * sibling of `#root` under `<body>` (measured live, 2026-09-20), while React 18
 * binds its listeners to the container given to `createRoot`. A native element
 * rendered inside a modal therefore receives no synthetic events at all — the
 * design-system components in the same modal keep working only because their
 * `@lit/react` wrappers bind real listeners.
 *
 * The node is tracked by a CALLBACK ref, not by reading `inputRef.current`
 * inside the effect. A caller may render `input` conditionally — the table's
 * `node-command-select.tsx` only renders it while the grid is in edit mode —
 * and such a caller re-renders in place rather than remounting, so on the first
 * commit `inputRef.current` is null, the effect binds nothing and never runs
 * again. That is a real regression against a React `onChange`, which is bound
 * to whichever node is rendered: it silently broke uploading a `.py` script
 * from a Command cell entered after the grid had already mounted.
 */
export const useFileInput = ({
  maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE_BYTES,
  onChange,
  ...props
}: InputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [node, setNode] = useState<HTMLInputElement | null>(null);
  // Writes both: `inputRef` is this hook's public handle (callers click it),
  // while the state is what re-runs the effect when the node is replaced.
  const setInputNode = useCallback((element: HTMLInputElement | null) => {
    inputRef.current = element;
    setNode(element);
  }, []);
  // Kept in a ref so the listener is attached once and still calls the latest
  // handler — a caller that rebuilds its callback every render would otherwise
  // re-subscribe on each one.
  const onChangeRef = useRef(onChange);
  const [t] = useTranslation();
  // Same reason as `onChangeRef`: kept in a ref so switching language does not
  // detach and re-attach the listener.
  const translateRef = useRef(t);

  useEffect(() => {
    onChangeRef.current = onChange;
    translateRef.current = t;
  }, [onChange, t]);

  useEffect(() => {
    if (!node) return undefined;

    const handleChange = (event: Event) => {
      const { files } = event.target as HTMLInputElement;

      if (!files) return;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (file.size > maxFileSizeBytes) {
          sendUserMessage({
            type: 'error',
            // Interpolated, never concatenated: word order is not universal,
            // and this toast is one of the few user-facing strings in this app
            // that was still assembled in English by hand.
            text: String(
              translateRef.current('pro-studio/common:fileUpload.tooLarge', {
                name: file.name,
                limit: (maxFileSizeBytes / 1024 / 1024).toFixed(0),
                size: (file.size / 1024 / 1024).toFixed(2),
              })
            ),
            autoClose: 3000,
          });

          node.value = '';

          return;
        }
      }

      // A native `change` carries the same `target` the callers read `files`
      // from — the only part of the event either of them uses — so the cast
      // keeps this hook's contract unchanged for them.
      onChangeRef.current?.(
        event as unknown as React.ChangeEvent<HTMLInputElement>
      );
    };

    node.addEventListener('change', handleChange);

    return () => node.removeEventListener('change', handleChange);
  }, [maxFileSizeBytes, node]);

  const input = (
    <input
      test-id={`${props['test-id']}-input`}
      ref={setInputNode}
      {...props}
    />
  );

  return { input, inputRef };
};
